import { Modal as AModal } from 'antd';
import React, { FC, ImgHTMLAttributes, useMemo, useRef, useState } from 'react';

import SVGIcon from 'components/SVGIcon/SVGIcon';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import { ContainerState } from 'containers/ProductDetailsPage/types';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import translations from 'translations';
import utilMessages from 'utils/messages';
import Image from '../Image/Image';
import PlayVideoButton from './PlayVideoButton.svg';
import SlideButtonIcon from './slideButton.svg';

export const FrameImage = styled((props: ImgHTMLAttributes<HTMLImageElement>) => <img {...props} />)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0;
  pointer-events: none;
`;

const Modal = styled(AModal)`
  width: 100% !important;
  max-width: 600px;
  .ant-modal-footer {
    margin: 0px !important;
  }
  .ant-modal-content {
    padding: 20px !important;
  }
`;

const SwiperWrapper = styled.div`
  position: relative;
`;

const SlideButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 10px;
  ${({ nextButton }) =>
    nextButton &&
    `
    left: unset;
    right: 10px;
    transform: translateY(-50%) rotate(0deg) !important;
  `}
  top: 50%;
  transform: translateY(-50%) rotate(180deg);
  width: 28px;
  height: 28px;
  border-radius: 24px;
  z-index: 1000;
  background: rgba(29, 30, 32, 0.3);
  cursor: pointer;
  & > div > div > img {
    margin-left: 3px;
  }
`;

const ThumbnailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding-bottom: 10px;
  overflow: auto;
`;

const ThumbnailItem = styled(SwiperSlide)`
  height: 100px !important;
  width: 100px !important;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  ${({ active }) =>
    active &&
    `
    border: 1px solid #43A047
  `}
`;

const VideoThumbnailWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const VideoThumbnailButton = styled(SVGIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface IProps {
  product: ContainerState['product'];
  open: boolean;
  activeIndexProp: number;
  onClose: () => void;
}

const ProductImagesModal: FC<IProps> = (props) => {
  const { product, open, activeIndexProp, onClose } = props;
  const swiperRef = useRef(null as any);
  const videoRef = useRef(null as any);
  const [activeIndex, setActiveIndex] = useState(activeIndexProp);
  const showSlideButton = useMemo(() => {
    return {
      prev: activeIndex !== 0,
      next: product?.medias?.length && activeIndex < product?.medias?.length - 1,
    };
  }, [activeIndex, product?.medias]);
  const lang = localStorage.getItem('lang') || 'vi';
  const frameTagImage = lang === 'vi' ? product?.tagImageVn : product?.tagImageEn || '';

  const handleManipulateVideo = (currentIndex: number) => {
    if (videoRef.current) {
      if (currentIndex === 0) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleClickThumbnail = (index: number) => {
    setActiveIndex(index);
    if (swiperRef.current && swiperRef.current.slideTo) {
      swiperRef.current.slideTo(index, 500);
    }
    handleManipulateVideo(index);
  };

  return (
    <Modal open={open} footer={false} destroyOnClose={true} onCancel={onClose} centered>
      <div style={{ position: 'relative' }}>
        <SwiperWrapper>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            initialSlide={activeIndexProp}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex);
              handleManipulateVideo(swiper.activeIndex);
            }}
          >
            {product?.medias?.map((media, index) => (
              <SwiperSlide key={index}>
                {media?.type === 'VIDEO' ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    controls
                    style={{ width: '100%', maxHeight: '560px', height: '100%' }}
                    src={media?.url || ''}
                  />
                ) : (
                  <>
                    <Image
                      zoomIn={false}
                      responsive
                      src={!product.loading ? media?.url || getImageByCategory(product.category) : ''}
                      alt={product.name ?? translations(utilMessages.productDetailImage)}
                      nopadding
                    />
                    {frameTagImage && <FrameImage src={frameTagImage} alt={`${product.name} frame`} />}
                  </>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          {showSlideButton.prev && (
            <SlideButton onClick={() => handleClickThumbnail(activeIndex - 1)}>
              <SVGIcon src={SlideButtonIcon} width="20" height="20" />
            </SlideButton>
          )}
          {showSlideButton.next && (
            <SlideButton nextButton={true} onClick={() => handleClickThumbnail(activeIndex + 1)}>
              <SVGIcon src={SlideButtonIcon} width="20" height="20" />
            </SlideButton>
          )}
        </SwiperWrapper>
        <ThumbnailWrapper>
          {product?.medias?.map((media, index) => (
            <ThumbnailItem
              onClick={() => handleClickThumbnail(index)}
              key={index}
              style={{ width: 'fit-content' }}
              active={index === activeIndex}
            >
              {media?.type === 'VIDEO' ? (
                <VideoThumbnailWrapper>
                  <video style={{ width: '100%', height: '100%' }} src={media?.url || ''} />
                  <VideoThumbnailButton src={PlayVideoButton} />
                </VideoThumbnailWrapper>
              ) : (
                <>
                  <Image
                    zoomIn={false}
                    responsive
                    src={!product.loading ? media?.url || getImageByCategory(product.category) : ''}
                    alt={product.name ?? translations(utilMessages.productDetailImage)}
                    nopadding
                  />
                  {frameTagImage && <FrameImage src={frameTagImage} />}
                </>
              )}
            </ThumbnailItem>
          ))}
        </ThumbnailWrapper>
      </div>
    </Modal>
  );
};

export default ProductImagesModal;
